import React from 'react';
import {Link} from 'react-router-dom';
import {Box, CircularProgress} from '@mui/material';
import dayjs from 'dayjs';
import {adminTokens} from 'theme/adminTokens';

export type DashEvent = {
  id: number;
  name: string;
  slug: string;
  status: string;
  startDate: string;
  endDate: string;
  countSubEvents?: number;
};

export type DashEntry = {
  id: number;
  createdAt?: string;
  userName?: string;
  subEvent?: {
    name?: string;
    event?: {name?: string; slug?: string};
  };
};

export type DashDevice = {
  id: number;
  name?: string;
  battery?: string | null;
  last?: string | null;
};

export type StatusCounts = {
  all: number;
  live: number;
  upcoming: number;
  finished: number;
};

export type EventFilter = 'all' | 'live' | 'upcoming' | 'finished';

const FILTERS: {id: EventFilter; label: string; countKey: keyof StatusCounts}[] = [
  {id: 'all', label: 'All events', countKey: 'all'},
  {id: 'live', label: 'Live', countKey: 'live'},
  {id: 'upcoming', label: 'Upcoming', countKey: 'upcoming'},
  {id: 'finished', label: 'Finished', countKey: 'finished'}
];

export const shellButtonSx = {
  background: '#fff',
  border: '1px solid #dfe4e1',
  borderRadius: '7px',
  padding: '11px 15px',
  fontSize: 14,
  fontWeight: 600,
  color: '#34433b',
  fontFamily: 'inherit',
  cursor: 'pointer',
  lineHeight: 1.2
};

export const primaryButtonSx = {
  ...shellButtonSx,
  background: adminTokens.pink,
  color: '#fff',
  borderColor: adminTokens.pink,
  boxShadow: '0 3px 8px #ff00661a'
};

const panelSx = {
  background: '#fff',
  border: `1px solid ${adminTokens.line}`,
  borderRadius: '11px',
  overflow: 'hidden'
};

export const PanelMessage = ({children}: {children: React.ReactNode}) => (
  <Box sx={{padding: '50px', textAlign: 'center', color: '#89968d'}}>{children}</Box>
);

export const StatGrid = ({children}: {children: React.ReactNode}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {xs: '1fr 1fr', md: 'repeat(auto-fit, minmax(200px, 1fr))'},
      gap: {xs: '10px', md: '17px'},
      mb: '27px'
    }}
  >
    {children}
  </Box>
);

export const StatCard = ({label, value, foot}: {label: string; value: React.ReactNode; foot: React.ReactNode}) => (
  <Box sx={{border: `1px solid ${adminTokens.line}`, borderRadius: '10px', background: '#fff', padding: {xs: '15px', md: '21px 22px'}}}>
    <Box sx={{fontSize: 14, color: '#748079'}}>{label}</Box>
    <Box sx={{fontSize: {xs: 29, md: 34}, letterSpacing: '-1px', margin: '13px 0 11px', fontWeight: 600, lineHeight: 1}}>{value}</Box>
    <Box sx={{fontSize: 12, color: '#8b948e'}}>{foot}</Box>
  </Box>
);

const statusStyle = (status: string) => {
  const key = status.toLowerCase();
  if (key === 'live') return {background: '#e9f6ef', color: '#27815b'};
  if (key === 'upcoming') return {background: '#eef0ff', color: '#7474aa'};
  return {background: '#f0f3f1', color: '#738078'};
};

const StatusPill = ({status}: {status: string}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: 11,
      padding: '5px 8px',
      borderRadius: '5px',
      textTransform: 'capitalize',
      ...statusStyle(status)
    }}
  >
    {status.toLowerCase() === 'live' ? '●  ' : ''}
    {status}
  </Box>
);

export const EventsPanel = ({
  events,
  total,
  page,
  pageSize,
  counts,
  filter,
  loading,
  error,
  onFilter,
  onPage
}: {
  events: DashEvent[];
  total: number;
  page: number;
  pageSize: number;
  counts?: StatusCounts;
  filter: EventFilter;
  loading: boolean;
  error?: string;
  onFilter: (filter: EventFilter) => void;
  onPage: (page: number) => void;
}) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Box sx={panelSx}>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '23px 24px 20px', gap: 2}}>
        <Box>
          <Box component="h2" sx={{fontSize: 17, margin: '0 0 5px', letterSpacing: '-0.3px', fontWeight: 600}}>
            Events
          </Box>
          <Box sx={{color: adminTokens.muted, fontSize: 13, m: 0}}>Everything happening, and what’s next.</Box>
        </Box>
        <Box component={Link} to="/my_events" sx={{color: adminTokens.pink, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap'}}>
          View all events ↗
        </Box>
      </Box>
      <Box sx={{display: 'flex', gap: '24px', px: '24px', borderBottom: `1px solid ${adminTokens.line}`, overflow: 'auto'}}>
        {FILTERS.map((tab) => (
          <Box
            component="button"
            type="button"
            key={tab.id}
            onClick={() => onFilter(tab.id)}
            sx={{
              background: 'transparent',
              border: 0,
              borderBottom: filter === tab.id ? `2px solid ${adminTokens.pink}` : '2px solid transparent',
              color: filter === tab.id ? '#242e29' : '#8a928e',
              fontWeight: filter === tab.id ? 700 : 500,
              padding: '0 0 14px',
              fontSize: 13,
              fontFamily: 'inherit',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
            <Box
              component="span"
              sx={{fontSize: 11, background: '#f1f4f2', borderRadius: '4px', padding: '3px 5px', ml: '6px', fontWeight: 600}}
            >
              {counts?.[tab.countKey] ?? 0}
            </Box>
          </Box>
        ))}
      </Box>
      {loading && !events.length ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <PanelMessage>{error}</PanelMessage>
      ) : !events.length ? (
        <PanelMessage>No events in this view.</PanelMessage>
      ) : (
        <Box sx={{overflow: 'auto'}}>
          <Box component="table" sx={{borderCollapse: 'collapse', width: '100%', textAlign: 'left', whiteSpace: 'nowrap'}}>
            <Box component="thead">
              <Box component="tr">
                {['Event name', 'Date & time', 'Classes', 'Status', ''].map((heading) => (
                  <Box
                    component="th"
                    key={heading || 'open'}
                    sx={{background: '#fafbfa', color: '#8b958e', fontSize: 11, letterSpacing: '1px', padding: '15px 22px', fontWeight: 600}}
                  >
                    {heading.toUpperCase()}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {events.map((event) => {
                const start = dayjs(event.startDate);
                const end = dayjs(event.endDate);
                return (
                  <Box component="tr" key={event.id || event.slug}>
                    <Box component="td" sx={{padding: '18px 22px', borderBottom: '1px solid #f0f2f0', fontSize: 13}}>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <Box
                          sx={{
                            height: 40,
                            width: 39,
                            border: `1px solid ${adminTokens.line}`,
                            borderRadius: '6px',
                            textAlign: 'center',
                            paddingTop: '5px',
                            fontSize: 11,
                            color: '#9a9f9c',
                            flexShrink: 0,
                            lineHeight: 1.15
                          }}
                        >
                          {start.isValid() ? start.format('MMM').toUpperCase() : '—'}
                          <Box component="b" sx={{display: 'block', color: '#45524a', fontSize: 17}}>
                            {start.isValid() ? start.format('DD') : '—'}
                          </Box>
                        </Box>
                        <Box>
                          <Box
                            component={Link}
                            to={`/my_events/${event.slug}`}
                            sx={{
                              display: 'block',
                              fontSize: 14,
                              mb: '5px',
                              fontWeight: 600,
                              color: adminTokens.ink,
                              textDecoration: 'none',
                              '&:hover': {color: adminTokens.pink}
                            }}
                          >
                            {event.name}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box component="td" sx={{padding: '18px 22px', borderBottom: '1px solid #f0f2f0', fontSize: 13}}>
                      {start.isValid() ? start.format('D MMM YYYY') : '—'}
                      <Box component="small" sx={{display: 'block', fontSize: 12, color: '#99a099'}}>
                        {start.isValid() ? start.format('HH:mm') : '—'}
                        {end.isValid() ? ` – ${end.format('HH:mm')}` : ''}
                      </Box>
                    </Box>
                    <Box component="td" sx={{padding: '18px 22px', borderBottom: '1px solid #f0f2f0', fontSize: 13}}>
                      {event.countSubEvents ?? 0}
                    </Box>
                    <Box component="td" sx={{padding: '18px 22px', borderBottom: '1px solid #f0f2f0', fontSize: 13}}>
                      <StatusPill status={event.status || 'finished'} />
                    </Box>
                    <Box component="td" sx={{padding: '18px 22px', borderBottom: '1px solid #f0f2f0'}}>
                      <Box
                        component={Link}
                        to={`/my_events/${event.slug}`}
                        sx={{
                          background: '#fff',
                          border: `1px solid ${adminTokens.line}`,
                          borderRadius: '5px',
                          padding: '6px 9px',
                          fontSize: 12,
                          color: '#57645c',
                          textDecoration: 'none',
                          '&:hover': {textDecoration: 'none', filter: 'brightness(0.96)'}
                        }}
                      >
                        Open ↗
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{padding: '16px 24px', fontSize: 12, color: '#97a099', display: 'flex', justifyContent: 'space-between'}}>
        <span>
          Showing {events.length} of {total} events
        </span>
        <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
          <Box component="button" type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} sx={{...shellButtonSx, padding: '4px 8px'}}>
            ←
          </Box>
          <span>
            {page} / {pages}
          </span>
          <Box component="button" type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} sx={{...shellButtonSx, padding: '4px 8px'}}>
            →
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const entryInitials = (name?: string) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '•';
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

export const AttentionPanel = ({
  entries,
  count,
  updatingId,
  error,
  onDecide
}: {
  entries: DashEntry[];
  count: number;
  updatingId: number | null;
  error?: string;
  onDecide: (entry: DashEntry, accept: boolean) => void;
}) => (
  <Box sx={{...panelSx, mt: '23px'}}>
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '23px 24px 20px', gap: 2}}>
      <Box>
        <Box component="h2" sx={{fontSize: 17, margin: 0, letterSpacing: '-0.3px', display: 'flex', alignItems: 'center', gap: 1}}>
          Needs your attention
          <Box
            component="span"
            sx={{background: adminTokens.badgeBg, color: adminTokens.pink, fontSize: 12, padding: '3px 6px', borderRadius: '5px'}}
          >
            {count}
          </Box>
        </Box>
        <Box sx={{color: adminTokens.muted, fontSize: 13, mt: '5px'}}>New entries waiting for your approval.</Box>
      </Box>
      <Box component={Link} to="/entries" sx={{color: adminTokens.pink, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap'}}>
        View all entries ↗
      </Box>
    </Box>
    {error ? (
      <PanelMessage>{error}</PanelMessage>
    ) : !count ? (
      <PanelMessage>All entries reviewed. You’re up to date.</PanelMessage>
    ) : (
      entries.map((entry) => (
        <Box
          key={entry.id}
          sx={{display: 'flex', alignItems: 'center', gap: '12px', padding: '15px 24px', borderTop: '1px solid #f0f2f0'}}
        >
          <Box
            sx={{
              borderRadius: '50%',
              width: 34,
              height: 34,
              background: '#edf1ee',
              color: '#6c8073',
              display: {xs: 'none', sm: 'grid'},
              placeItems: 'center',
              fontSize: 12,
              flexShrink: 0
            }}
          >
            {entryInitials(entry.userName)}
          </Box>
          <Box sx={{flex: 1, minWidth: 0}}>
            <Box component="strong" sx={{fontSize: 13, display: 'block', mb: '5px'}}>
              {entry.userName || 'Unnamed entry'}
            </Box>
            <Box sx={{fontSize: 12, color: adminTokens.muted, m: 0}}>
              {entry.subEvent?.event?.name || 'Event'}
              <Box component="span" sx={{color: '#bdc6bf'}}>
                {' '}
                ·{' '}
              </Box>
              {entry.subEvent?.name || 'Class'}
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            disabled={updatingId === entry.id}
            onClick={() => onDecide(entry, false)}
            sx={{border: `1px solid ${adminTokens.line}`, background: '#fff', borderRadius: '5px', padding: '7px 10px', color: '#557060', fontSize: 12, fontFamily: 'inherit'}}
          >
            Decline
          </Box>
          <Box
            component="button"
            type="button"
            disabled={updatingId === entry.id}
            onClick={() => onDecide(entry, true)}
            sx={{
              border: '1px solid #ffd7e6',
              background: '#fff6f9',
              borderRadius: '5px',
              padding: '7px 10px',
              color: adminTokens.pink,
              fontSize: 12,
              fontFamily: 'inherit'
            }}
          >
            ✓ Approve
          </Box>
        </Box>
      ))
    )}
  </Box>
);

const LiveMap = () => (
  <Box sx={{height: 125, margin: '15px 15px 0', position: 'relative', overflow: 'hidden', background: '#26382d', borderRadius: '7px'}}>
    <Box component="svg" viewBox="0 0 300 135" sx={{width: '100%', height: '100%'}}>
      <defs>
        <pattern id="contour" width="80" height="60" patternUnits="userSpaceOnUse">
          <path d="M-20 55Q20-30 70 20T140 5M-20 65Q20-20 70 30T140 15M-20 75Q20-10 70 40T140 25M-20 85Q20 0 70 50T140 35" fill="none" stroke="#4c604e" strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect width="300" height="135" fill="url(#contour)" />
      <path d="M48 104L75 75 115 89 140 33 189 56 216 27 255 41" fill="none" stroke="#ff467f" strokeWidth="2" strokeDasharray="4 3" />
      <g fill="#ff0066" stroke="#ffc2d4" strokeWidth="2">
        <circle cx="75" cy="75" r="4" />
        <circle cx="140" cy="33" r="4" />
        <circle cx="216" cy="27" r="4" />
      </g>
      <circle cx="189" cy="56" r="9" fill="#ff006633" />
      <circle cx="189" cy="56" r="3" fill="#ff75a0" />
    </Box>
  </Box>
);

export const LiveCard = ({event}: {event: DashEvent}) => {
  const start = dayjs(event.startDate);
  const monitor = `${import.meta.env.VITE_DOMAIN_EVENTS || ''}${event.slug}`;

  return (
    <Box sx={{...panelSx, background: '#202e28', color: '#fff', border: 0}}>
      <Box sx={{display: 'flex', justifyContent: 'space-between', padding: '23px 24px 10px'}}>
        <Box sx={{color: '#a6d6b8', fontSize: 11, letterSpacing: '1.2px'}}>
          <Box component="span" sx={{display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#24a67b', mr: '6px'}} />
          LIVE RIGHT NOW
        </Box>
      </Box>
      <Box component="h3" sx={{fontSize: 21, lineHeight: 1.4, margin: '10px 24px 5px', fontWeight: 500}}>
        {event.name}
      </Box>
      <Box sx={{color: '#9db0a4', fontSize: 13, ml: '24px'}}>
        {start.isValid() ? `Started ${start.format('D MMM YYYY [at] HH:mm')}` : 'Live event'}
      </Box>
      <LiveMap />
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', fontSize: 12}}>
        <span>{event.countSubEvents ? `${event.countSubEvents} classes` : 'On course'}</span>
        <Box
          component="a"
          href={monitor}
          target="_blank"
          rel="noreferrer"
          sx={{border: 0, background: '#3a4a40', color: '#fff', padding: '8px 11px', borderRadius: '5px', fontSize: 12, textDecoration: 'none'}}
        >
          Live monitor ↗
        </Box>
      </Box>
    </Box>
  );
};

export type DeviceHealth = {
  total: number;
  online: number;
  low: number;
  offline: number;
  hasTelemetry: boolean;
};

export const classifyDevices = (devices: DashDevice[]): DeviceHealth => {
  let online = 0;
  let low = 0;
  let offline = 0;
  const now = Date.now();
  devices.forEach((device) => {
    const battery = device.battery != null && device.battery !== '' ? Number(device.battery) : null;
    const last = device.last ? dayjs(device.last) : null;
    const hasBattery = battery != null && !Number.isNaN(battery);
    const hasLast = Boolean(last?.isValid());
    if (!hasBattery && !hasLast) return;
    const stale = !hasLast || now - last!.valueOf() > 12 * 60 * 60 * 1000;
    if (stale) offline += 1;
    else if (hasBattery && battery! < 20) low += 1;
    else online += 1;
  });
  const reported = online + low + offline;
  return {total: devices.length, online, low, offline, hasTelemetry: reported > 0};
};

export const DeviceHealthPanel = ({health}: {health: DeviceHealth}) => {
  const tracked = health.online + health.low + health.offline;
  return (
    <Box sx={panelSx}>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '23px 24px 20px'}}>
        <Box component="h2" sx={{fontSize: 17, margin: 0}}>
          Device health
        </Box>
        <Box component={Link} to="/my_devices" sx={{color: adminTokens.pink, fontSize: 13, fontWeight: 600}}>
          ↗
        </Box>
      </Box>
      {health.hasTelemetry && tracked > 0 && (
        <Box sx={{display: 'flex', height: 5, borderRadius: '5px', overflow: 'hidden', margin: '3px 24px 10px', gap: '3px'}}>
          <Box sx={{background: '#278267', flex: health.online || 0.001}} />
          <Box sx={{background: '#efbd5f', flex: health.low || 0.001}} />
          <Box sx={{background: '#e6eae7', flex: health.offline || 0.001}} />
        </Box>
      )}
      {health.hasTelemetry ? (
        [
          ['Online & tracking', health.online, '#24a67b'],
          ['Low battery', health.low, '#edb34b'],
          ['Offline', health.offline, '#b7c1ba']
        ].map(([label, value, color], index, rows) => (
          <Box
            key={String(label)}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              mx: '24px',
              py: '13px',
              borderBottom: index === rows.length - 1 ? 0 : '1px solid #eff1ef',
              color: '#76837a',
              pb: index === rows.length - 1 ? '22px' : '13px'
            }}
          >
            <span>
              <Box component="span" sx={{display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: String(color), mr: '6px'}} />
              {label}
            </span>
            <Box component="b" sx={{color: '#35483b', fontWeight: 500}}>
              {value}
            </Box>
          </Box>
        ))
      ) : (
        <Box sx={{mx: '24px', pb: '22px', fontSize: 13, color: '#76837a'}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', py: '13px'}}>
            <span>Registered devices</span>
            <Box component="b" sx={{color: '#35483b', fontWeight: 500}}>
              {health.total}
            </Box>
          </Box>
          Battery and last-seen times are not included in the device response, so online status stays on the devices page.
        </Box>
      )}
    </Box>
  );
};

export type ActivityItem = {
  id: string;
  tone: string;
  title: string;
  detail: string;
  time: string;
};

export const ActivityPanel = ({items}: {items: ActivityItem[]}) => (
  <Box sx={panelSx}>
    <Box sx={{padding: '23px 24px 8px'}}>
      <Box component="h2" sx={{fontSize: 17, margin: 0}}>
        Recent activity
      </Box>
    </Box>
    <Box sx={{padding: '3px 24px 20px'}}>
      {!items.length ? (
        <Box sx={{py: 2, color: '#89968d', fontSize: 13}}>Nothing new yet.</Box>
      ) : (
        items.map((item) => (
          <Box key={item.id} sx={{padding: '10px 0', display: 'flex', gap: '10px', fontSize: 12, color: '#77847b', lineHeight: 1.6}}>
            <Box component="span" sx={{color: item.tone}}>
              ●
            </Box>
            <Box>
              <Box component="b" sx={{color: '#44594a', fontWeight: 500}}>
                {item.title}
              </Box>{' '}
              {item.detail}
              <Box component="small" sx={{display: 'block', color: '#a0aaa3', fontSize: 11}}>
                {item.time}
              </Box>
            </Box>
          </Box>
        ))
      )}
    </Box>
  </Box>
);
